import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import { LOCATIONS } from "./seo/content";
import { ArrowUpRight, MapPin, Check, Clock } from "lucide-react";

function renderInline(text) {
  const nodes = [];
  let rest = text;
  let i = 0;
  const linkRe = /\[([^\]]+)\]\(([^)]+)\)/;
  const boldRe = /\*\*([^*]+)\*\*/;
  while (rest.length) {
    const l = rest.match(linkRe);
    const b = rest.match(boldRe);
    const lIdx = l ? l.index : Infinity;
    const bIdx = b ? b.index : Infinity;
    if (lIdx === Infinity && bIdx === Infinity) {
      nodes.push(<React.Fragment key={i++}>{rest}</React.Fragment>);
      break;
    }
    if (lIdx < bIdx) {
      nodes.push(<React.Fragment key={i++}>{rest.substring(0, lIdx)}</React.Fragment>);
      nodes.push(
        <Link key={i++} to={l[2]} className="text-terracotta underline underline-offset-2 hover:no-underline">
          {l[1]}
        </Link>
      );
      rest = rest.substring(lIdx + l[0].length);
    } else {
      nodes.push(<React.Fragment key={i++}>{rest.substring(0, bIdx)}</React.Fragment>);
      nodes.push(
        <strong key={i++} className="text-ink font-medium">
          {b[1]}
        </strong>
      );
      rest = rest.substring(bIdx + b[0].length);
    }
  }
  return nodes;
}

function useLocationSchema(loc, lang) {
  useEffect(() => {
    if (!loc) return;
    const t = loc[lang] || loc.de;
    const ld = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: `Ayurveda Aargau – für Frauen aus ${loc.slug.charAt(0).toUpperCase()}${loc.slug.slice(1)}`,
      description: t.metaDescription,
      areaServed: [
        { "@type": "City", name: "Aarau" },
        { "@type": "City", name: "Suhr" },
        { "@type": "City", name: "Buchs" },
        { "@type": "City", name: "Küttigen" },
      ],
      hasMap: "https://maps.app.goo.gl/",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Obere Feldeggstrasse 13",
        postalCode: "5614",
        addressLocality: "Sarmenstorf",
        addressRegion: "AG",
        addressCountry: "CH",
      },
    };
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = "ld-location";
    s.text = JSON.stringify(ld);
    document.head.appendChild(s);
    return () => s.remove();
  }, [loc, lang]);
}

export default function LocationPage() {
  const { slug } = useParams();
  const { lang } = useLang();
  const loc = LOCATIONS[slug];
  useLocationSchema(loc, lang);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!loc) return <Navigate to="/standort" replace />;
  const t = loc[lang] || loc.de;

  return (
    <div data-testid={`location-${slug}`}>
      <section className="bg-sand-soft border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.3em] text-terracotta mb-5">— Standort</div>
            <h1 className="font-display font-light text-4xl lg:text-6xl text-ink leading-tight">{t.h1}</h1>
            <p className="mt-8 text-lg text-ink-muted leading-relaxed max-w-2xl">{t.lead}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/booking"
                className="inline-flex items-center gap-2 px-8 py-4 bg-terracotta text-white rounded-full hover:bg-terracotta-hover transition-colors font-medium"
                data-testid={`location-${slug}-book`}
              >
                Termin buchen
                <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
              <Link
                to="/standort"
                className="inline-flex items-center gap-2 px-8 py-4 border border-ink text-ink rounded-full hover:bg-ink hover:text-white transition-colors font-medium"
              >
                <MapPin className="w-4 h-4" strokeWidth={1.5} />
                Beide Standorte ansehen
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 lg:px-12 py-16">
        {t.sections.map((sec, i) => (
          <section key={i} className="mb-14">
            <h2 className="font-display text-3xl lg:text-4xl text-ink leading-tight mb-6">{sec.h2}</h2>
            {sec.body.map((p, j) => (
              <p key={j} className="text-ink-muted leading-relaxed mb-4">
                {renderInline(p)}
              </p>
            ))}
            {sec.list && (
              <ul className="mt-4 space-y-3">
                {sec.list.map((li, k) => (
                  <li key={k} className="flex gap-3 text-ink-muted leading-relaxed">
                    <span className="w-5 h-5 rounded-full bg-terracotta/10 text-terracotta flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3" strokeWidth={2.5} />
                    </span>
                    <span className="text-sm">{renderInline(li)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>

      <section className="bg-sand-soft border-t border-border">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-16">
          <h2 className="font-display text-3xl lg:text-4xl text-ink leading-tight">Häufige Fragen</h2>
          <div className="mt-10 space-y-6">
            {t.faqs.map((f, i) => (
              <div key={i} className="bg-white border border-border rounded-md p-6">
                <h3 className="font-display text-xl text-ink mb-2">{f.q}</h3>
                <p className="text-sm text-ink-muted leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
